import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Sparkles, Loader2, Plus, Copy, Check, Edit3, Trash2, Save, X,
  BarChart3, Globe, Smartphone, Lock, ArrowRight, QrCode,
  AlertTriangle, Zap,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area,
} from "recharts";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CustomDomainPanel } from "@/components/qr/CustomDomainPanel";

const GOLD = "#F2C94C";
const CYAN = "#00E5FF";

/**
 * /my-qr-codes — Dynamic QR dashboard.
 *
 * Access gates (server enforces + client mirrors for UX):
 *   • Not activated → upgrade CTA to /pricing (Dynamic QR $50 activation)
 *   • Activated but subscription lapsed → resubscribe banner + read-only history
 *   • Activated + subscription active → full create/edit/analytics
 */
export default function MyQRCodes() {
  const navigate = useNavigate();
  const { user, loading: authLoading, checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState({ activated: false, subscription_active: false, status: "never_activated" });
  const [qrs, setQrs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newDest, setNewDest] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(null); // qr_id | "create" | "resubscribe"

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/qr/dynamic/list");
      setAccess(data.access || { activated: false, subscription_active: false });
      setQrs(data.qrs || []);
    } catch (e) {
      toast.error("Couldn't load your QR codes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/?intent=signup"); return; }
    loadList();
  }, [authLoading, user, loadList, navigate]);

  // Handle post-checkout return with ?paid=dynamic-qr-activation or ?paid=dynamic-qr-monthly
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid");
    if (paid?.startsWith("dynamic-qr")) {
      toast.success("Payment received — dynamic QR unlocked.");
      checkAuth?.();
      loadList();
      window.history.replaceState({}, "", "/my-qr-codes");
    }
  }, [checkAuth, loadList]);

  const purchaseActivation = async () => {
    setBusy("create-activation");
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "dynamic_qr_activation",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout.");
      setBusy(null);
    }
  };

  const purchaseSubscription = async () => {
    setBusy("resubscribe");
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "dynamic_qr_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout.");
      setBusy(null);
    }
  };

  const createQr = async () => {
    const dest = newDest.trim();
    if (!dest) { toast.error("Enter a destination URL."); return; }
    if (!/^https?:\/\//i.test(dest)) { toast.error("URL must start with http:// or https://"); return; }
    setBusy("create");
    try {
      const { data } = await api.post("/qr/dynamic/create", { destination: dest, label: newLabel.trim() || null });
      toast.success("Dynamic QR minted");
      setNewDest("");
      setNewLabel("");
      setCreating(false);
      // Push to top of list
      setQrs((prev) => [{
        qr_id: data.qr_id,
        short_code: data.short_code,
        short_url: data.short_url,
        destination: data.destination,
        label: data.label,
        total_scans: 0,
        active: true,
        created_at: data.created_at,
      }, ...prev]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || "Failed to create QR code.");
    } finally {
      setBusy(null);
    }
  };

  const updateDestination = async (qr, newValue) => {
    const dest = (newValue || "").trim();
    if (!dest) { toast.error("Destination required"); return; }
    if (!/^https?:\/\//i.test(dest)) { toast.error("URL must start with http:// or https://"); return; }
    setBusy(qr.qr_id);
    try {
      await api.patch(`/qr/dynamic/${qr.qr_id}`, { destination: dest });
      toast.success("Destination updated");
      setQrs((prev) => prev.map((q) => q.qr_id === qr.qr_id ? { ...q, destination: dest } : q));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const deleteQr = async (qr) => {
    if (!window.confirm(`Delete this QR code? Scan history for "${qr.label || qr.short_code}" will be permanently erased.`)) return;
    setBusy(qr.qr_id);
    try {
      await api.delete(`/qr/dynamic/${qr.qr_id}`);
      setQrs((prev) => prev.filter((q) => q.qr_id !== qr.qr_id));
      if (expandedId === qr.qr_id) setExpandedId(null);
      toast.success("QR deleted");
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const totalScans = useMemo(() => qrs.reduce((sum, q) => sum + (q.total_scans || 0), 0), [qrs]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F2C94C] animate-spin" />
      </div>
    );
  }

  // ── Gate A: Not activated at all → upgrade CTA ───────────────────
  if (!access.activated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] glyph-bg text-white">
        <Navbar />
        <main className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14">
          <Header />
          <UpgradeCTA busy={busy} onActivate={purchaseActivation} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] glyph-bg text-white">
      <Navbar />
      <main className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <Header />

        {/* Subscription lapsed banner */}
        {access.activated && !access.subscription_active && (
          <div
            data-testid="mqc-reactivate-banner"
            className="mb-6 rounded-2xl border border-[#EB5757]/40 bg-[#EB5757]/[0.08] p-5 sm:p-6"
          >
            <div className="flex items-start gap-3 flex-wrap">
              <AlertTriangle className="w-5 h-5 text-[#EB5757] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg mb-1">Your Dynamic QR subscription has lapsed</div>
                <p className="font-body text-sm text-white/80 leading-relaxed">
                  Existing QR codes have paused redirecting — visitors see a "temporarily inactive" screen.
                  Your data and scan history are safe. Resubscribe for $25/mo to bring them back to life.
                </p>
              </div>
              <button
                data-testid="mqc-resubscribe-btn"
                onClick={purchaseSubscription}
                disabled={busy === "resubscribe"}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_28px_-4px_rgba(242,201,76,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {busy === "resubscribe" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                RESUBSCRIBE · $25/MO
              </button>
            </div>
          </div>
        )}

        {/* Stats strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard testid="mqc-stat-total" icon={<QrCode className="w-4 h-4" />} label="TOTAL QR CODES" value={qrs.length} accent={GOLD} />
          <StatCard testid="mqc-stat-scans" icon={<BarChart3 className="w-4 h-4" />} label="TOTAL SCANS" value={totalScans} accent={CYAN} />
          <StatCard
            testid="mqc-stat-status"
            icon={access.subscription_active ? <Zap className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            label="SUBSCRIPTION"
            value={access.subscription_active ? "ACTIVE" : "PAUSED"}
            accent={access.subscription_active ? "#27AE60" : "#EB5757"}
            small
          />
        </div>

        {/* Custom Domain panel */}
        <div className="mb-6">
          <CustomDomainPanel
            subscriptionActive={access.subscription_active}
            onDomainChange={loadList}
          />
        </div>

        {/* Create card */}
        {access.subscription_active && (
          <div className="mb-6" data-testid="mqc-create-panel">
            {!creating ? (
              <button
                onClick={() => setCreating(true)}
                data-testid="mqc-create-toggle"
                className="w-full rounded-2xl border-2 border-dashed border-[#00E5FF]/25 bg-[#00E5FF]/[0.02] hover:border-[#00E5FF]/60 hover:bg-[#00E5FF]/[0.05] py-5 flex items-center justify-center gap-2 transition-all"
              >
                <Plus className="w-4 h-4 text-[#00E5FF]" />
                <span className="font-mono-hy text-[10px] text-[#00E5FF] tracking-[0.22em]">MINT A NEW DYNAMIC QR</span>
              </button>
            ) : (
              <div className="rounded-2xl border border-[#00E5FF]/25 bg-white/[0.02] p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-hy text-[10px] text-[#00E5FF] tracking-[0.24em]">NEW DYNAMIC QR</span>
                  <button
                    onClick={() => { setCreating(false); setNewDest(""); setNewLabel(""); }}
                    className="w-7 h-7 rounded-full border border-white/[0.15] hover:border-white flex items-center justify-center"
                    aria-label="Cancel"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="space-y-3">
                  <input
                    value={newDest}
                    onChange={(e) => setNewDest(e.target.value)}
                    placeholder="https://your-destination.com/campaign"
                    data-testid="mqc-new-dest"
                    className="w-full px-4 py-3 rounded-xl border border-white/[0.08] bg-[#0A0A0A]/70 focus:border-[#00E5FF]/60 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/25 font-mono text-[13px] text-white placeholder:text-white/40"
                  />
                  <input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="Label — optional. e.g. 'Spring campaign poster'"
                    maxLength={80}
                    data-testid="mqc-new-label"
                    className="w-full px-4 py-3 rounded-xl border border-white/[0.08] bg-[#0A0A0A]/70 focus:border-[#F2C94C]/60 focus:outline-none focus:ring-2 focus:ring-[#F2C94C]/25 font-body text-[14px] text-white placeholder:text-white/40"
                  />
                  <button
                    onClick={createQr}
                    disabled={busy === "create"}
                    data-testid="mqc-new-submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-[#00E5FF] hover:bg-[#33EDFF] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_28px_-4px_rgba(0,229,255,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    MINT DYNAMIC QR
                  </button>
                  <p className="font-mono-hy text-[9px] text-white/60 tracking-widest text-center">
                    YOU'LL GET A SHORT URL LIKE {window.location.origin.replace(/^https?:\/\//, "")}/q/ABC12345 — ENCODE THAT INTO YOUR QR
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* QR list */}
        <div className="space-y-3" data-testid="mqc-list">
          {qrs.length === 0 ? (
            <EmptyState onCreate={() => setCreating(true)} enabled={access.subscription_active} />
          ) : (
            qrs.map((qr) => (
              <QrRow
                key={qr.qr_id}
                qr={qr}
                expanded={expandedId === qr.qr_id}
                onToggle={() => setExpandedId(expandedId === qr.qr_id ? null : qr.qr_id)}
                onUpdate={updateDestination}
                onDelete={deleteQr}
                busy={busy === qr.qr_id}
                editingAllowed={access.subscription_active}
              />
            ))
          )}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-2xl border border-white/[0.08] bg-white/[0.02]">
          <div className="min-w-0">
            <div className="font-display text-lg mb-1">Need a static QR instead?</div>
            <p className="font-body text-sm text-white/70">
              Fixed URL — no dashboard, no monthly fee. Use the free QR Generator.
            </p>
          </div>
          <Link
            to="/qr-generator"
            data-testid="mqc-static-link"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl border border-[#F2C94C]/40 hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.08] font-mono-hy text-[10px] tracking-[0.22em] text-[#F2C94C] transition-all"
          >
            OPEN STATIC QR GENERATOR
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* ── Header ──────────────────────────────────────────────────────── */
function Header() {
  return (
    <div className="mb-8 sm:mb-10">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 mb-3">
        <BarChart3 className="w-3 h-3 text-[#00E5FF]" />
        <span className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.24em]">
          DYNAMIC QR · ANALYTICS
        </span>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-3">
        My{" "}
        <span
          className="bg-clip-text text-transparent"
          style={{ backgroundImage: "linear-gradient(120deg, #F2C94C 0%, #E27D60 45%, #00E5FF 100%)" }}
        >
          QR Codes
        </span>
      </h1>
      <p className="font-body text-base sm:text-lg text-white leading-relaxed max-w-2xl">
        Edit destinations anytime without reprinting. Track every scan by country and device.
      </p>
    </div>
  );
}

/* ── Upgrade CTA (never activated) ───────────────────────────────── */
function UpgradeCTA({ busy, onActivate }) {
  return (
    <div
      data-testid="mqc-upgrade-cta"
      className="rounded-3xl border border-[#00E5FF]/25 p-6 sm:p-10 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(70% 100% at 100% 100%, rgba(0,229,255,0.20), transparent 60%)," +
          "radial-gradient(70% 100% at 0% 0%, rgba(88,28,135,0.45), transparent 55%)," +
          "linear-gradient(120deg, #0e0a1c 0%, #120E1F 100%)",
      }}
    >
      <div className="grid md:grid-cols-[auto,1fr,auto] items-center gap-6 md:gap-10 relative">
        <div
          className="w-24 h-24 md:w-32 md:h-32 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #00E5FF 0%, #7c3aed 50%, #F2C94C 100%)", boxShadow: "0 0 40px -10px rgba(0,229,255,0.75)" }}
        >
          <QrCode className="w-14 h-14 text-[#0A0812]" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <h3 className="font-display text-2xl md:text-3xl text-white mb-2">Unlock Dynamic QR</h3>
          <p className="font-body text-sm text-white/80 mb-4 max-w-xl">
            $50 one-time activation, then $25/month keeps redirects live + analytics running.
            Free & credit-only accounts can only make static QR codes.
          </p>
          <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-4 font-mono-hy text-[10px] text-white tracking-widest">
            {[
              "Unlimited dynamic QR codes",
              "Live destination editing",
              "Scan analytics dashboard",
              "Country + device tracking",
              "Monthly scan reports",
            ].map((f) => (
              <li key={f} className="flex items-center gap-2"><Check className="w-3 h-3 text-[#00E5FF]" /> {f.toUpperCase()}</li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col items-start md:items-end gap-2 md:min-w-[200px]">
          <div className="font-display text-3xl text-[#00E5FF] font-bold">$50</div>
          <div className="font-mono-hy text-[9px] text-white/70 tracking-widest">+ $25/MO</div>
          <button
            onClick={onActivate}
            disabled={busy === "create-activation"}
            data-testid="mqc-activate-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#00E5FF] hover:bg-[#33EDFF] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_28px_-4px_rgba(0,229,255,0.85)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {busy === "create-activation" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            ACTIVATE NOW
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Empty state ─────────────────────────────────────────────────── */
function EmptyState({ onCreate, enabled }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] py-14 text-center" data-testid="mqc-empty">
      <div className="mx-auto w-14 h-14 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/[0.05] flex items-center justify-center mb-4">
        <QrCode className="w-6 h-6 text-[#00E5FF]" />
      </div>
      <div className="font-display text-xl mb-1">No dynamic QR codes yet</div>
      <p className="font-body text-sm text-white/70 max-w-md mx-auto mb-5">
        Mint your first dynamic code — pick a destination, get a short URL, encode it in your QR.
        Change the destination anytime.
      </p>
      {enabled && (
        <button
          onClick={onCreate}
          data-testid="mqc-empty-cta"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#00E5FF] hover:bg-[#33EDFF] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold transition-all"
        >
          <Plus className="w-4 h-4" /> MINT YOUR FIRST QR
        </button>
      )}
    </div>
  );
}

/* ── Individual QR row ───────────────────────────────────────────── */
function QrRow({ qr, expanded, onToggle, onUpdate, onDelete, busy, editingAllowed }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(qr.destination);
  const [copied, setCopied] = useState(false);

  useEffect(() => setDraft(qr.destination), [qr.destination]);

  const copyShort = async () => {
    try {
      await navigator.clipboard.writeText(qr.short_url);
      setCopied(true);
      toast.success("Short link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden"
      data-testid={`mqc-row-${qr.short_code}`}
    >
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-display text-base text-white truncate">
              {qr.label || qr.short_code}
            </span>
            <span className="font-mono-hy text-[8px] text-[#F2C94C] tracking-widest bg-[#F2C94C]/10 px-2 py-0.5 rounded-full">
              /q/{qr.short_code}
            </span>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-[12px] text-white/70 truncate min-w-0" title={qr.destination}>
              → {qr.destination}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={copyShort}
            data-testid={`mqc-copy-${qr.short_code}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#F2C94C]/30 hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.06] font-mono-hy text-[9px] tracking-[0.18em] text-[#F2C94C] transition-all"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "COPIED" : "COPY"}
          </button>
          <button
            onClick={onToggle}
            data-testid={`mqc-analytics-${qr.short_code}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all ${
              expanded
                ? "border-[#00E5FF] bg-[#00E5FF]/[0.1] text-[#00E5FF]"
                : "border-[#00E5FF]/30 hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.06] text-[#00E5FF]"
            } font-mono-hy text-[9px] tracking-[0.18em]`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            {qr.total_scans} SCANS
          </button>
          <button
            onClick={() => onDelete(qr)}
            disabled={busy}
            aria-label="Delete"
            data-testid={`mqc-delete-${qr.short_code}`}
            className="w-9 h-9 rounded-xl border border-white/[0.08] hover:border-[#EB5757] hover:bg-[#EB5757]/[0.08] hover:text-[#EB5757] transition-all flex items-center justify-center disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Edit destination */}
      <div className="px-4 sm:px-5 pb-3">
        {editing ? (
          <div className="flex flex-col sm:flex-row gap-2" data-testid={`mqc-edit-${qr.short_code}`}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="flex-1 px-3 py-2 rounded-xl border border-white/[0.08] bg-[#0A0A0A]/70 focus:border-[#00E5FF]/60 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/25 font-mono text-[13px] text-white"
            />
            <div className="flex gap-2">
              <button
                onClick={async () => { await onUpdate(qr, draft); setEditing(false); }}
                disabled={busy}
                data-testid={`mqc-edit-save-${qr.short_code}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#00E5FF] hover:bg-[#33EDFF] text-[#0A0A0A] font-mono-hy text-[9px] tracking-[0.22em] font-bold transition-all"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                SAVE
              </button>
              <button
                onClick={() => { setEditing(false); setDraft(qr.destination); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-white/[0.15] font-mono-hy text-[9px] tracking-[0.22em] text-white transition-all hover:border-white"
              >
                CANCEL
              </button>
            </div>
          </div>
        ) : (
          editingAllowed ? (
            <button
              onClick={() => setEditing(true)}
              data-testid={`mqc-edit-toggle-${qr.short_code}`}
              className="inline-flex items-center gap-1.5 font-mono-hy text-[9px] tracking-[0.18em] text-white/70 hover:text-[#F2C94C] transition-colors"
            >
              <Edit3 className="w-3 h-3" /> EDIT DESTINATION
            </button>
          ) : (
            <span className="font-mono-hy text-[9px] tracking-[0.18em] text-white/40">
              <Lock className="w-3 h-3 inline mr-1" /> RESUBSCRIBE TO EDIT
            </span>
          )
        )}
      </div>

      {expanded && <AnalyticsDrawer qrId={qr.qr_id} />}
    </div>
  );
}

/* ── Analytics drawer ────────────────────────────────────────────── */
function AnalyticsDrawer({ qrId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/qr/dynamic/${qrId}/analytics?days=30`);
        setData(data);
      } catch {
        toast.error("Couldn't load analytics.");
      } finally {
        setLoading(false);
      }
    })();
  }, [qrId]);

  if (loading) {
    return (
      <div className="border-t border-white/[0.06] p-8 flex justify-center">
        <Loader2 className="w-5 h-5 text-[#F2C94C] animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const maxScans = Math.max(1, ...data.series.map((s) => s.scans));

  return (
    <div className="border-t border-white/[0.06] p-5 sm:p-6 bg-[#0A0812]/50" data-testid={`mqc-analytics-drawer-${data.short_code}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Line chart */}
        <div className="lg:col-span-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono-hy text-[10px] text-[#00E5FF] tracking-[0.24em]">
              SCANS · LAST 30 DAYS
            </span>
            <span className="font-mono-hy text-[10px] text-[#F2C94C] tracking-widest">
              {data.total_scans} TOTAL · PEAK {maxScans}/DAY
            </span>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id={`grad-${qrId}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CYAN} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={CYAN} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10, fontFamily: "Space Mono, monospace" }}
                  tickFormatter={(v) => v.slice(5)}
                  interval={Math.floor(data.series.length / 6)}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10, fontFamily: "Space Mono, monospace" }}
                  allowDecimals={false}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "#120E15",
                    border: "1px solid rgba(242,201,76,0.25)",
                    borderRadius: 12,
                    fontFamily: "Space Mono, monospace",
                    fontSize: 12,
                    color: "#fff",
                  }}
                  cursor={{ stroke: GOLD, strokeWidth: 1, strokeDasharray: "4 4" }}
                  labelStyle={{ color: GOLD }}
                />
                <Area type="monotone" dataKey="scans" stroke="none" fill={`url(#grad-${qrId})`} />
                <Line type="monotone" dataKey="scans" stroke={CYAN} strokeWidth={2} dot={{ r: 3, fill: GOLD, stroke: GOLD }} activeDot={{ r: 5, fill: GOLD, stroke: CYAN, strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Countries */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 lg:col-span-2">
          <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.24em] mb-3 flex items-center gap-2">
            <Globe className="w-3.5 h-3.5" /> TOP COUNTRIES
          </div>
          {data.top_countries.length === 0 ? (
            <p className="font-body text-sm text-white/50">No scans yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_countries.map((c) => {
                const pct = data.total_scans ? Math.round((c.scans / data.total_scans) * 100) : 0;
                return (
                  <li key={c.country} className="flex items-center gap-3">
                    <span className="font-mono text-[13px] text-white w-10">{c.country}</span>
                    <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${GOLD} 0%, ${CYAN} 100%)`,
                        }}
                      />
                    </div>
                    <span className="font-mono-hy text-[10px] text-white/80 w-16 text-right tracking-widest">
                      {c.scans} · {pct}%
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Devices */}
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="font-mono-hy text-[10px] text-[#00E5FF] tracking-[0.24em] mb-3 flex items-center gap-2">
            <Smartphone className="w-3.5 h-3.5" /> DEVICES
          </div>
          {data.top_devices.length === 0 ? (
            <p className="font-body text-sm text-white/50">No scans yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_devices.map((d) => {
                const pct = data.total_scans ? Math.round((d.scans / data.total_scans) * 100) : 0;
                return (
                  <li key={d.device} className="flex items-center gap-3">
                    <span className="font-mono text-[11px] text-white w-16 capitalize">{d.device}</span>
                    <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#00E5FF]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="font-mono-hy text-[10px] text-white/80 w-10 text-right tracking-widest">
                      {pct}%
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── StatCard reused from Profile ────────────────────────────────── */
function StatCard({ icon, label, value, accent, testid, small }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-30 blur-2xl"
        style={{ background: accent }}
      />
      <div className="relative flex items-center gap-2 mb-3">
        <span style={{ color: accent }}>{icon}</span>
        <span className="font-mono-hy text-[9px] text-white/70 tracking-[0.24em]">{label}</span>
      </div>
      <div
        className={`relative font-display leading-none tracking-tight ${small ? "text-2xl sm:text-3xl" : "text-4xl sm:text-5xl"}`}
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}
